import { useCallback } from 'react';

import { stripHtmlTags } from '@change-corgi/core/html';
import { useI18n } from '@change-corgi/core-react/i18n';

import { usePsfSharePageData } from 'src/app/pages/psf/share/pageContext';
import type { ShareChannel } from 'src/app/pages/psf/share/shared/types';
import { useCurrentUser } from 'src/app/shared/hooks/session';
import type { ShareActionsOptions } from 'src/app/shared/hooks/share';

// eslint-disable-next-line max-lines-per-function
export function useGetShareContent(): ShareActionsOptions<ShareChannel>['getContent'] {
	const { translate } = useI18n();
	const {
		data: {
			petition: {
				displayTitle,
				description,
				signatureState: { signatureGoal },
				bestStoryShareVisual,
			},
		},
	} = usePsfSharePageData();

	const currentUser = useCurrentUser();
	const currentUserName = currentUser?.shortDisplayName || '';
	const descriptionText = stripHtmlTags(description);

	return useCallback(
		// eslint-disable-next-line complexity
		(channel, type, { url }) => {
			switch (channel) {
				case 'whatsapp':
					return translate('fe.components.petition_share.share_text_whatsapp');
				case 'twitter':
					return translate('fe.components.petition_share.share_text_twitter', { petition_title: displayTitle });
				case 'sms':
					return translate('fe.components.petition_share.share_text_sms');
				case 'line':
					return translate('fe.components.petition_share.share_text_line');
				case 'facebookMessenger':
					return translate('fe.components.petition_share.share_text');
				case 'email':
					if (type === 'title') return displayTitle;
					return translate('fe.components.petition_share.email_body', {
						title: displayTitle,
						number: signatureGoal.displayedLocalized,
						url,
						from: currentUserName,
					});
				case 'nextdoor':
					if (descriptionText.length > 900) {
						return translate(
							'corgi.petition.starter_dashboard.grow_petition.share_modal.share_text.nextdoor.truncated_body',
							{
								title: displayTitle,
								description: descriptionText.slice(0, descriptionText.lastIndexOf(' ', 900)),
							},
						);
					}
					return translate('corgi.petition.starter_dashboard.grow_petition.share_modal.share_text.nextdoor.body', {
						title: displayTitle,
						description: descriptionText,
					});
				case 'tiktok':
				case 'instagramStories':
					if (type === 'image') {
						return bestStoryShareVisual?.url || '';
					}
					return '';
				default:
					return '';
			}
		},
		[translate, displayTitle, descriptionText, signatureGoal.displayedLocalized, currentUserName, bestStoryShareVisual],
	);
}
